/**
 * @fileoverview Hero primary CTA with sign-up click analytics.
 * @description Renders the CONTROL copy in static/SSR HTML (so the homepage
 * carries no runtime data and stays fully prerendered — see
 * `assignments-client-store.ts` for why a server-streamed variant slot is not
 * an option), then swaps to the assigned variant's copy client-side once the
 * assignment fetch resolves. Control visitors never see a swap; treatment
 * visitors see the standard client-side-testing copy flip shortly after
 * hydration — the exposure beacon fires from the same fetch, so recorded
 * exposures match the copy the visitor ends up seeing.
 */

"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { useSyncExternalStore } from "react"

import { siteConfig } from "~/lib/config/site"
import { Button } from "~/components/Button"
import type { HomepageHeroCtaVariant } from "~/lib/experiments/build-flags"
import {
  areAssignmentsFetched,
  emitWhenAssignmentsReady,
  getFetchedAssignments,
  subscribeAssignments,
} from "~/lib/experiments/assignments-client-store"
import { HERO_CTA_COPY } from "~/lib/experiments/hero-cta"
import { getExperimentDefinition } from "~/lib/experiments/registry"
import { sendEvent, trackMarketingCtaClick } from "~/lib/gtag"

const EXPERIMENT_KEY = "homepage-hero-cta"

/**
 * Whether the hero experiment is live. When it is NOT running (paused/draft), a
 * pre-fetch click has no hero assignment to wait for, so the intent-to-treat
 * navigation hold is skipped — the CTA emits immediately and navigates without
 * the up-to-2s wait for unrelated assignment plumbing. Read from the static
 * registry (a cheap find), so a status flip needs no code change here.
 */
function heroExperimentRunning(): boolean {
  return getExperimentDefinition(EXPERIMENT_KEY)?.status === "running"
}

function assignedVariant(): string | undefined {
  return getFetchedAssignments().find((a) => a.experiment_id === EXPERIMENT_KEY)
    ?.variant_id
}

/**
 * Primary hero CTA — tracks `sign_up_click` on navigation intent.
 *
 * @returns {React.ReactNode} Linked button.
 */
export function HeroCtaLink(): React.ReactNode {
  const router = useRouter()
  const variant = useSyncExternalStore(
    subscribeAssignments,
    assignedVariant,
    // Server snapshot: control — matches the prerendered HTML.
    () => undefined,
  )
  const cta =
    HERO_CTA_COPY[(variant ?? "control") as HomepageHeroCtaVariant] ??
    HERO_CTA_COPY.control

  const handleSignUpClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ): void => {
    // Site-intelligence marketing CTA event FIRST, exactly as the
    // pre-experiment `HeroRequestAccessCta` fired it: the engagement/journey
    // fan-out treats `button_click` (not `sign_up_click`) as the high-intent
    // signal, so replacing it would silently drop the primary homepage CTA
    // from those reports while the experiment runs (#233 `r3516537437`).
    trackMarketingCtaClick({
      placement: "homepage_hero",
      destinationPath: siteConfig.baseLinks.requestAccess,
      linkText: cta.label,
    })
    // Click-time URL, captured BEFORE any deferred emit: the full URL (not a
    // human-readable string) keeps the production hostName that GA4-only
    // rollups filter on (#233 `r3424834234`).
    const clickPageLocation = window.location.href
    // Captured at CLICK time: on a pre-assignment click this stays the
    // CONTROL copy even if the fetch later resolves to treatment.
    const clickedLabel = cta.label
    const preAssignmentClick = !areAssignmentsFetched()
    const emit = (): void => {
      sendEvent({
        action: "sign_up_click",
        label: "Hero Sign-up Button",
        page_location: clickPageLocation,
        element_id: "hero-primary-cta",
        params: {
          cta_label: clickedLabel,
          // Attribution stays INTENT-TO-TREAT: the deferred emit carries the
          // ASSIGNED variant's dimensions even though the visitor clicked
          // the pre-swap control copy — assignment-based counting is what
          // keeps the analysis unbiased (excluding these clicks would
          // undercount exactly the treatment arm's fastest converters,
          // since only treatment visitors experience the swap window). This
          // flag + the click-time `cta_label` make those clicks queryable,
          // so the analyzer can quantify the flicker rate and sensitivity-
          // test with them excluded (#233 `r3519603536`).
          ...(preAssignmentClick ? { pre_assignment_click: true } : {}),
        },
      })
    }

    // Emit immediately (no navigation hold) when assignments are already
    // fetched OR when the experiment isn't running — a paused/draft hero has no
    // assignment to wait for, so holding Request Access navigation for the
    // assignments fetch would delay the primary CTA for nothing.
    if (areAssignmentsFetched() || !heroExperimentRunning()) {
      emit()
      return
    }

    const isPlainLeftClick =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey

    if (!isPlainLeftClick) {
      // Modified click (new tab/window): THIS tab stays on the click
      // surface, so the deferred emit still resolves dimensions against the
      // right pathname — let the browser handle the navigation.
      emitWhenAssignmentsReady(emit)
      return
    }

    // Plain click racing the assignments fetch: hold the NAVIGATION, not
    // just the event. Emitting after Next moves to the target route would
    // resolve dimensions/ledger targets against the WRONG pathname — and
    // the exposure beacon (keyed to the current pathname) would never fire
    // for the click surface at all, orphaning the conversion out of the
    // exposure-joined ledger count (#233 `r3517374490`/`r3517397807`).
    // Staying on the page until the store resolves (2s cap via
    // `emitWhenAssignmentsReady`) lets the beacon record the exposure and
    // the emit enrich correctly; the fetch is a fast pure read already in
    // flight since mount, so the added wait is the fetch's remaining
    // latency, not a full round-trip from zero.
    event.preventDefault()
    emitWhenAssignmentsReady(() => {
      emit()
      // Yield one task so the beacon's effect (flushed by the same store
      // notification) can kick off its exposure work on the click surface
      // before the route changes. If it still loses the race, the visitor's
      // next visit to the surface records the exposure (session-deduped) —
      // the conversion is orphan-logged, never silently miscounted.
      setTimeout(() => {
        router.push(siteConfig.baseLinks.requestAccess)
      }, 0)
    })
  }

  return (
    <Button asChild className="h-10 font-semibold">
      <Link
        href={siteConfig.baseLinks.requestAccess}
        onClick={handleSignUpClick}
        prefetch={false}
        // Visible `label` is the accessible name — do not override with
        // `aria-label`; experiment copy variants must stay discoverable to
        // assistive tech and e2e role queries by their rendered text.
        title={cta.ariaLabel}
      >
        {cta.label}
      </Link>
    </Button>
  )
}
